"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { AuthorizedUser } from "@/core/auth/action.auth";
import { signOut } from "@/core/auth/action.auth";
import { browserSupabaseClient } from "@/core/auth/supa.client";
import { fetchMeiliUserToken } from "@/core/search";
import { useAuth } from "@/state/globalStore";

const MEILI_REFRESH_BUFFER_MS = 5 * 60 * 1000;

interface AuthDataContextType {
  isAuthReady: boolean;
  isAuthLoading: boolean;
}

const AuthDataContext = createContext<AuthDataContextType | undefined>(
  undefined,
);

interface AuthDataProviderProps {
  children: React.ReactNode;
  initialUser?: AuthorizedUser | null;
  initialAccessToken?: string | null;
  initialRefreshToken?: string | null;
  meiliUserToken?: string | null;
  meiliUserTokenExpiresAt?: string | null;
}

function hasFreshMeiliToken(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) {
    return false;
  }

  const expiresAtMs = Date.parse(expiresAt);
  if (Number.isNaN(expiresAtMs)) {
    return false;
  }

  return expiresAtMs - Date.now() > MEILI_REFRESH_BUFFER_MS;
}

export function AuthDataProvider({
  children,
  initialUser = null,
  initialAccessToken = null,
  initialRefreshToken = null,
  meiliUserToken = null,
  meiliUserTokenExpiresAt = null,
}: AuthDataProviderProps) {
  const {
    setUser,
    setTokens,
    logout,
    isInitialized,
    accessToken,
    refreshToken,
    meiliUserToken: storedMeiliUserToken,
    meiliUserTokenExpiresAt: storedMeiliUserTokenExpiresAt,
  } = useAuth();

  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const isRefreshingMeiliTokenRef = useRef(false);

  const safelyClearServerSession = async () => {
    try {
      await signOut();
    } catch {}
  };

  const refreshMeiliSession = useCallback(
    async ({
      nextAccessToken,
      nextRefreshToken,
    }: {
      nextAccessToken?: string | null;
      nextRefreshToken?: string | null;
    } = {}) => {
      const accessTokenToUse = nextAccessToken ?? accessToken;
      const refreshTokenToUse = nextRefreshToken ?? refreshToken;

      if (!accessTokenToUse || !refreshTokenToUse) {
        return null;
      }

      if (isRefreshingMeiliTokenRef.current) {
        return null;
      }

      if (
        storedMeiliUserToken &&
        hasFreshMeiliToken(storedMeiliUserTokenExpiresAt)
      ) {
        if (
          accessTokenToUse !== accessToken ||
          refreshTokenToUse !== refreshToken
        ) {
          setTokens(
            accessTokenToUse,
            refreshTokenToUse,
            storedMeiliUserToken,
            storedMeiliUserTokenExpiresAt,
          );
        }

        return {
          userToken: storedMeiliUserToken,
          expiresAt: storedMeiliUserTokenExpiresAt,
        };
      }

      isRefreshingMeiliTokenRef.current = true;
      try {
        const meiliSession = await fetchMeiliUserToken(accessTokenToUse);

        if (!meiliSession?.userToken) {
          setTokens(
            accessTokenToUse,
            refreshTokenToUse,
            storedMeiliUserToken,
            storedMeiliUserTokenExpiresAt,
          );
          return null;
        }

        setTokens(
          accessTokenToUse,
          refreshTokenToUse,
          meiliSession.userToken,
          meiliSession.expiresAt,
        );
        return meiliSession;
      } finally {
        isRefreshingMeiliTokenRef.current = false;
      }
    },
    [
      accessToken,
      refreshToken,
      setTokens,
      storedMeiliUserToken,
      storedMeiliUserTokenExpiresAt,
    ],
  );

  useEffect(() => {
    const initializeAuth = async () => {
      setIsAuthLoading(true);

      if (
        initialUser &&
        initialAccessToken &&
        initialRefreshToken &&
        meiliUserToken
      ) {
        setUser(initialUser);
        setTokens(
          initialAccessToken,
          initialRefreshToken,
          meiliUserToken,
          meiliUserTokenExpiresAt,
        );
        setIsAuthReady(true);
        setIsAuthLoading(false);
        return;
      }

      setIsAuthReady(false);
      setIsAuthLoading(false);
      await safelyClearServerSession();
      logout();
    };

    initializeAuth();
  }, [
    initialUser,
    initialAccessToken,
    initialRefreshToken,
    meiliUserToken,
    meiliUserTokenExpiresAt,
    setUser,
    setTokens,
    logout,
  ]);

  useEffect(() => {
    const { data: authListener } = browserSupabaseClient.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "TOKEN_REFRESHED" && session) {
          await refreshMeiliSession({
            nextAccessToken: session.access_token,
            nextRefreshToken: session.refresh_token,
          });
        } else if (event === "SIGNED_OUT") {
          await safelyClearServerSession();
          logout();
          setIsAuthReady(false);
        } else if (event === "INITIAL_SESSION" && !session) {
        } else if (event === "SIGNED_IN" && session) {
          await refreshMeiliSession({
            nextAccessToken: session.access_token,
            nextRefreshToken: session.refresh_token,
          });
        } else if (!session) {
          await safelyClearServerSession();
          logout();
          setIsAuthReady(false);
        }
      },
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [logout, refreshMeiliSession]);

  useEffect(() => {
    if (
      !isAuthReady ||
      !accessToken ||
      !refreshToken ||
      !storedMeiliUserTokenExpiresAt
    ) {
      return;
    }

    const expiresAtMs = Date.parse(storedMeiliUserTokenExpiresAt);
    if (Number.isNaN(expiresAtMs)) {
      return;
    }

    const refreshInMs = Math.max(
      expiresAtMs - Date.now() - MEILI_REFRESH_BUFFER_MS,
      0,
    );

    const timeoutId = window.setTimeout(() => {
      void refreshMeiliSession({
        nextAccessToken: accessToken,
        nextRefreshToken: refreshToken,
      });
    }, refreshInMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    accessToken,
    isAuthReady,
    refreshMeiliSession,
    refreshToken,
    storedMeiliUserTokenExpiresAt,
  ]);

  return (
    <AuthDataContext.Provider
      value={{
        isAuthReady: isAuthReady && isInitialized,
        isAuthLoading,
      }}
    >
      {children}
    </AuthDataContext.Provider>
  );
}

export function useAuthData() {
  const context = useContext(AuthDataContext);
  if (context === undefined) {
    throw new Error("useAuthData must be used within an AuthDataProvider");
  }
  return context;
}
